/**
 * Settings › Scoring: the rules that give leads and people points, the bands
 * a score falls in, and how a person's score fades.
 *
 * Lead rules score a lead when it arrives, before routing, so a group or
 * destination can filter on the score ("Score is more than 50" for the
 * premium tier). Person rules add points for what a person does (opens an
 * email, has an open deal), and are part of the CRM area; with CRM off only
 * lead rules and the bands are here, because routing on a score is lead
 * distribution's own.
 *
 * The preview beside the lead rules scores the newest real leads with the
 * saved rules. Nothing it shows is stored.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon, Alert, Badge, Button, Card, ColorSwatch, Grid, Group, Menu, NumberInput, SegmentedControl, Select,
  Stack, Switch, Table, Text, TextInput, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconArrowDown, IconArrowUp, IconDots, IconPencil, IconPlus, IconTrash,
} from '@tabler/icons-react';
import { useCampaigns } from '../../api/queries';
import {
  useScorePreview, useScoringEdits, useScoringRules, useScoringSettings, type ScoreBand, type ScoreObject,
  type ScoringRule, type ScoringSettings,
} from '../../api/scoring';
import { useVocab } from '../../vocab';
import { UsedByButton } from '../workflows/UsedByPanel';
import { ScorePreview } from './ScorePreview';
import { ScoringRuleDrawer } from './ScoringRuleDrawer';
import { describeConditions, signed, useScoringPermissions } from './scoringShared';

const COLOURS = ['gray', 'red', 'pink', 'grape', 'violet', 'indigo', 'blue', 'cyan', 'teal', 'green', 'lime', 'yellow', 'orange'];

export function ScoringSettingsTab() {
  const v = useVocab();
  const perms = useScoringPermissions();
  const rules = useScoringRules(perms.sees);
  const settings = useScoringSettings(perms.sees);
  const all = rules.data?.rules ?? [];
  const leadRules = all.filter((r) => r.object === 'submission');
  const personRules = all.filter((r) => r.object === 'person');
  const anyLeadRule = leadRules.some((r) => r.active);
  const preview = useScorePreview(anyLeadRule ? {} : null, perms.sees);
  const [editing, setEditing] = useState<{ object: ScoreObject; rule: ScoringRule | null } | null>(null);

  return (
    <Stack gap="md" maw={1180}>
      <Text size="sm" c="dimmed">
        {`Rules give points. A ${v.one('lead')} is scored when it arrives, before routing, so ${v.a('routeGroup')} or ${v.a('destination')} can take only hot ${v.many('lead')}.`}
        {perms.people ? ` A ${v.one('person')}'s score starts from their best ${v.one('lead')} and adds what they have done since.` : ''}
        {` Scores are for your own team: ${v.many('buyer')} and ${v.many('supplier')} never see them.`}
      </Text>
      {rules.error && <Alert color="red" variant="light">{rules.error.message}</Alert>}

      <Grid gutter="md">
        <Grid.Col span={{ base: 12, lg: 7 }}>
          <Stack gap="md">
            <RuleSection
              title={`${v.One('lead')} rules`}
              hint={`Scored once, when the ${v.one('lead')} arrives.`}
              object="submission"
              rules={leadRules}
              loading={rules.isPending}
              onNew={() => setEditing({ object: 'submission', rule: null })}
              onEdit={(r) => setEditing({ object: 'submission', rule: r })}
            />
            {perms.people && (
              <RuleSection
                title={`${v.One('person')} rules`}
                hint={`Worked out again when they arrive again, open or click an email, or a deal changes.`}
                object="person"
                rules={personRules}
                loading={rules.isPending}
                onNew={() => setEditing({ object: 'person', rule: null })}
                onEdit={(r) => setEditing({ object: 'person', rule: r })}
              />
            )}
            {settings.data && <BandsCard settings={settings.data} people={perms.people} />}
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 5 }}>
          <Card padding="md">
            <Stack gap="xs">
              <Text fw={600}>How recent {v.many('lead')} score</Text>
              {anyLeadRule ? (
                <ScorePreview
                  data={preview.data}
                  loading={preview.isFetching}
                  error={preview.error}
                  settings={settings.data}
                />
              ) : (
                <Text size="sm" c="dimmed">{`Switch on a ${v.one('lead')} rule to see how the newest ${v.many('lead')} would score.`}</Text>
              )}
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      <ScoringRuleDrawer
        opened={editing !== null}
        object={editing?.object ?? 'submission'}
        rule={editing?.rule ?? null}
        allowTypes={editing?.object === 'person' ? (rules.data?.person_types ?? ['field']) : (rules.data?.submission_types ?? ['field'])}
        facts={rules.data?.person_facts ?? []}
        onClose={() => setEditing(null)}
      />
    </Stack>
  );
}

function RuleSection({
  title, hint, object, rules, loading, onNew, onEdit,
}: {
  title: string;
  hint: string;
  object: ScoreObject;
  rules: ScoringRule[];
  loading: boolean;
  onNew: () => void;
  onEdit: (r: ScoringRule) => void;
}) {
  const v = useVocab();
  const edits = useScoringEdits();
  const campaigns = useCampaigns(object === 'submission' && rules.some((r) => r.campaign_ids.length > 0));
  const names = useMemo(
    () => new Map((campaigns.data?.campaigns ?? []).map((c) => [c.id, c.name])),
    [campaigns.data],
  );

  async function move(i: number, by: -1 | 1) {
    const ids = rules.map((r) => r.id);
    const j = i + by;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j] ?? '', ids[i] ?? ''];
    try {
      await edits.reorder.mutateAsync({ object, ids });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not moved', message: (e as Error).message });
    }
  }

  async function toggle(r: ScoringRule, active: boolean) {
    try {
      await edits.activate.mutateAsync({ id: r.id, active });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not changed', message: (e as Error).message });
    }
  }

  async function del(r: ScoringRule) {
    if (!window.confirm(`Delete ${r.name}? Scores already given keep it in their breakdown.`)) return;
    try {
      await edits.remove.mutateAsync(r.id);
      notifications.show({ color: 'teal', title: 'Deleted', message: r.name });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not deleted', message: (e as Error).message });
    }
  }

  return (
    <Card padding="md" data-testid={`rules-${object}`}>
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <div>
            <Text fw={600}>{title}</Text>
            <Text size="xs" c="dimmed">{hint}</Text>
          </div>
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={onNew}>New rule</Button>
        </Group>
        {!loading && rules.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            {object === 'submission'
              ? `No rules yet. Every ${v.one('lead')} scores 0 and routing works exactly as it does now.`
              : `No rules yet. A ${v.one('person')}'s score is their best ${v.one('lead')}'s.`}
          </Text>
        ) : (
          <Table verticalSpacing={6} horizontalSpacing="xs">
            <Table.Tbody>
              {rules.map((r, i) => (
                <Table.Tr key={r.id} data-testid={`rule-row-${r.name}`} style={r.active ? undefined : { opacity: 0.55 }}>
                  <Table.Td w={64}>
                    <Badge
                      variant="light"
                      color={r.points > 0 ? 'green' : 'red'}
                      radius="sm"
                      tt="none"
                      fullWidth
                      style={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {signed(r.points)}
                    </Badge>
                  </Table.Td>
                  <Table.Td style={{ minWidth: 0 }}>
                    <Text size="sm" fw={500} truncate="end">{r.name}</Text>
                    <Text size="xs" c="dimmed" lineClamp={2}>
                      {describeConditions(r.conditions)}
                      {r.campaign_ids.length > 0
                        ? `. Only ${r.campaign_ids.map((c) => names.get(c) ?? c).join(', ')}`
                        : ''}
                    </Text>
                  </Table.Td>
                  <Table.Td w={150}>
                    <Group gap={2} justify="flex-end" wrap="nowrap">
                      <Tooltip label={r.active ? 'On' : 'Off: adds nothing'} withArrow>
                        <Switch
                          size="xs"
                          aria-label={`${r.name} on`}
                          checked={r.active}
                          onChange={(e) => toggle(r, e.currentTarget.checked)}
                        />
                      </Tooltip>
                      <ActionIcon variant="subtle" size="sm" aria-label={`Move ${r.name} up`} disabled={i === 0} onClick={() => move(i, -1)}>
                        <IconArrowUp size={14} />
                      </ActionIcon>
                      <ActionIcon variant="subtle" size="sm" aria-label={`Move ${r.name} down`} disabled={i === rules.length - 1} onClick={() => move(i, 1)}>
                        <IconArrowDown size={14} />
                      </ActionIcon>
                      <ActionIcon variant="subtle" size="sm" aria-label={`Edit ${r.name}`} onClick={() => onEdit(r)}>
                        <IconPencil size={14} />
                      </ActionIcon>
                      <Menu position="bottom-end" withinPortal>
                        <Menu.Target>
                          <ActionIcon variant="subtle" size="sm" aria-label={`More for ${r.name}`}><IconDots size={14} /></ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => del(r)}>Delete</Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Card>
  );
}

function BandsCard({ settings, people }: { settings: ScoringSettings; people: boolean }) {
  const v = useVocab();
  const { saveSettings } = useScoringEdits();
  const [draft, setDraft] = useState<ScoringSettings>(settings);
  const [error, setError] = useState('');
  useEffect(() => setDraft(settings), [settings]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(settings);

  const setBand = (i: number, patch: Partial<ScoreBand>) =>
    setDraft((d) => ({ ...d, bands: d.bands.map((b, j) => (j === i ? { ...b, ...patch } : b)) }));

  async function save() {
    setError('');
    try {
      await saveSettings.mutateAsync(draft);
      notifications.show({ color: 'teal', title: 'Saved', message: 'Bands and decay' });
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <Card padding="md" data-testid="bands-card">
      <Stack gap="sm">
        <div>
          <Text fw={600}>Bands</Text>
          <Text size="xs" c="dimmed">
            Each band starts at its lowest score and runs up to the next. The first takes everything below the second.
          </Text>
        </div>
        {draft.bands.map((b, i) => (
          <Group key={i} gap="xs" wrap="nowrap" align="flex-end">
            <TextInput
              size="xs"
              label={i === 0 ? 'Name' : undefined}
              aria-label={`Band ${i + 1} name`}
              value={b.label}
              onChange={(e) => setBand(i, { label: e.currentTarget.value, key: '' })}
              style={{ flex: 1 }}
            />
            <NumberInput
              size="xs"
              label={i === 0 ? 'From' : undefined}
              aria-label={`Band ${i + 1} lowest score`}
              value={i === 0 ? '' : (b.min ?? '')}
              placeholder={i === 0 ? 'Anything lower' : undefined}
              disabled={i === 0}
              allowDecimal={false}
              onChange={(n) => setBand(i, { min: typeof n === 'number' ? n : null })}
              w={130}
            />
            <Select
              size="xs"
              label={i === 0 ? 'Colour' : undefined}
              aria-label={`Band ${i + 1} colour`}
              w={110}
              data={COLOURS.map((c) => ({ value: c, label: c }))}
              value={b.colour}
              allowDeselect={false}
              leftSection={<ColorSwatch size={10} color={`var(--mantine-color-${b.colour}-6)`} />}
              onChange={(c) => c && setBand(i, { colour: c })}
              renderOption={({ option }) => (
                <Group gap={6}><ColorSwatch size={10} color={`var(--mantine-color-${option.value}-6)`} />{option.label}</Group>
              )}
            />
            <ActionIcon
              variant="subtle"
              color="red"
              size="md"
              aria-label={`Remove band ${b.label}`}
              disabled={draft.bands.length <= 1}
              onClick={() => setDraft((d) => ({ ...d, bands: d.bands.filter((_, j) => j !== i) }))}
            >
              <IconTrash size={14} />
            </ActionIcon>
            {b.key && (
              <UsedByButton
                refType="band"
                refId={b.key}
                hideWhenNone
                title={`${v.Many('workflow')} that use ${b.label}`}
                testId={`band-used-by-${b.key}`}
              />
            )}
          </Group>
        ))}
        <Group>
          <Button
            size="xs"
            variant="default"
            leftSection={<IconPlus size={14} />}
            disabled={draft.bands.length >= 8}
            onClick={() => setDraft((d) => {
              const last = d.bands[d.bands.length - 1];
              const min = (last?.min ?? 0) + 25;
              return { ...d, bands: [...d.bands, { key: '', label: 'New band', min, colour: 'violet' }] };
            })}
          >
            Add band
          </Button>
        </Group>

        {people && (
          <>
            <div>
              <Text fw={600} mt="xs">{`${v.One('person')} scores`}</Text>
              <Text size="xs" c="dimmed">
                {`Where a ${v.one('person')}'s score starts, and how it fades while they do not arrive again. Decay brings a score down towards 0, never below.`}
              </Text>
            </div>
            <SegmentedControl
              size="xs"
              data={[
                { value: 'max', label: `Best ${v.one('lead')}` },
                { value: 'latest', label: `Newest ${v.one('lead')}` },
              ]}
              value={draft.person_base ?? 'max'}
              onChange={(b) => setDraft((d) => ({ ...d, person_base: b as ScoringSettings['person_base'] }))}
              style={{ alignSelf: 'flex-start' }}
            />
            <Group gap="xs" align="flex-end">
              <NumberInput
                size="xs"
                label="Lose points"
                description="0 switches decay off"
                min={0}
                max={1000}
                allowDecimal={false}
                value={draft.decay?.points ?? 0}
                onChange={(n) => setDraft((d) => ({ ...d, decay: { points: typeof n === 'number' ? n : 0, every_days: d.decay?.every_days ?? 30 } }))}
                w={150}
              />
              <NumberInput
                size="xs"
                label="Every so many days"
                description={`Without a new ${v.one('lead')}`}
                min={1}
                max={3650}
                allowDecimal={false}
                value={draft.decay?.every_days ?? 30}
                onChange={(n) => setDraft((d) => ({ ...d, decay: { points: d.decay?.points ?? 0, every_days: typeof n === 'number' ? n : 30 } }))}
                w={170}
              />
            </Group>
          </>
        )}
        {error && <Alert color="red" variant="light">{error}</Alert>}
        <Group justify="flex-end">
          <Button size="xs" variant="subtle" disabled={!dirty} onClick={() => setDraft(settings)}>Discard</Button>
          <Button size="xs" disabled={!dirty} loading={saveSettings.isPending} onClick={save}>Save</Button>
        </Group>
      </Stack>
    </Card>
  );
}
